import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';
import { Hook } from '@rossum/api-client/hooks';
import { Alert, Dialog, Stack } from '@rossum/ui/material';
import SingleCheckboxControl from '../../../components/ReactHookForm/controls/SingleCheckboxControl';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import ActionDialog from '../../../components/UI/ActionDialog';
import ActionDialogActions from '../../../components/UI/ActionDialog/ActionDialogActions';
import { useDuplicateHook } from '../hooks/useDuplicateHook';

const duplicateExtensionFormSchema = z.object({
  name: z.string(),
  copySecrets: z.boolean(),
  copyDependencies: z.boolean(),
  copyQueues: z.boolean(),
});

type DuplicateExtensionDialogFormProps = {
  extension: Hook;
  onClose: () => void;
};

const DuplicateExtensionDialogForm = ({
  onClose,
  extension,
}: DuplicateExtensionDialogFormProps) => {
  const intl = useIntl();

  const { mutate: duplicateHook, isLoading } = useDuplicateHook({
    onSuccess: onClose,
  });

  const { handleSubmit, control } = useForm({
    mode: 'onSubmit',
    resolver: zodResolver(duplicateExtensionFormSchema),
    defaultValues: {
      copySecrets: false,
      copyDependencies: false,
      copyQueues: false,
      name: intl.formatMessage(
        { id: 'containers.settings.extensions.duplicateExtension.defaultName' },
        { name: extension.name || 'Extension' }
      ),
    },
  });

  return (
    <form
      onSubmit={handleSubmit(payload =>
        duplicateHook({
          hookId: extension.id,
          payload,
        })
      )}
    >
      <ActionDialog
        dialogTitle={intl.formatMessage({
          id: 'containers.settings.extensions.duplicateExtension.title',
        })}
        onCancel={onClose}
        dialogColor="primary"
        dialogActions={
          <ActionDialogActions
            confirmButtonProps={{
              type: 'submit',
              disabled: isLoading,
            }}
            confirmButtonText={intl.formatMessage({
              id: 'containers.settings.extensions.duplicateExtension.confirm',
            })}
            cancelButtonText={intl.formatMessage({
              id: 'containers.settings.extensions.duplicateExtension.cancel',
            })}
          />
        }
        dataCy="duplicate-queue-close-btn"
      >
        <Stack spacing={2} direction="column" py={1}>
          <TextFieldControl
            ControllerProps={{ control, name: 'name' }}
            FieldLabelProps={{ layout: 'floating' }}
            label={intl.formatMessage({
              id: 'containers.settings.extensions.duplicateExtension.nameLabel',
            })}
            autoComplete="off"
          />
          <Stack sx={{ pl: 1 }}>
            <SingleCheckboxControl
              ControllerProps={{ control, name: 'copySecrets' }}
              FieldLabelProps={{ layout: 'none' }}
              label={intl.formatMessage({
                id: 'containers.settings.extensions.duplicateExtension.secretsLabel',
              })}
            />
            <SingleCheckboxControl
              ControllerProps={{ control, name: 'copyDependencies' }}
              FieldLabelProps={{ layout: 'none' }}
              label={intl.formatMessage({
                id: 'containers.settings.extensions.duplicateExtension.dependencyLabel',
              })}
            />
            <SingleCheckboxControl
              ControllerProps={{ control, name: 'copyQueues' }}
              FieldLabelProps={{ layout: 'none' }}
              label={intl.formatMessage({
                id: 'containers.settings.extensions.duplicateExtension.queuesLabel',
              })}
            />
          </Stack>
          <Alert severity="warning">
            {intl.formatMessage({
              id: 'containers.settings.extensions.duplicateExtension.description',
            })}
          </Alert>
        </Stack>
      </ActionDialog>
    </form>
  );
};

type DuplicateExtensionDialogProps = {
  extension: Hook | undefined;
  open: boolean;
  onClose: () => void;
};

export const DuplicateExtensionDialog = ({
  extension,
  open,
  onClose,
}: DuplicateExtensionDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: 480, minHeight: 300, position: 'fixed' },
          elevation: 2,
        },
      }}
    >
      {open && extension ? (
        <DuplicateExtensionDialogForm extension={extension} onClose={onClose} />
      ) : null}
    </Dialog>
  );
};
